import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ReferenceStock } from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/model/ReferenceStock';
import { useTheme } from 'styled-components';
import { Ban, ExadisStoreIcon, GlobeIcon } from 'assets/icons';
import { getUserCountry } from 'domains/user';
import { Box, Flex, Icon, Text } from 'UI';
import { ERROR, LOADING, SearchData } from 'utils';

const containsConfirmedQuantity = (stock: SearchData<ReferenceStock> | undefined) =>
  (stock?.data?.warehouses ?? []).filter((warehouse) => warehouse.confirmedQuantity > 0).length > 0;

export function isEmptyStock(stock: SearchData<ReferenceStock> | undefined): boolean {
  if (stock === undefined || stock?.searchStatus === LOADING || stock?.searchStatus === ERROR) {
    return false;
  }
  return !containsConfirmedQuantity(stock);
}

export interface EmptyStockProps {
  isExadisPickUp: boolean | undefined;
  isDrumOil: boolean | undefined;
}

export function EmptyStock({ isExadisPickUp, isDrumOil }: Readonly<EmptyStockProps>) {
  const theme = useTheme();
  const { t } = useTranslation();
  const userCountry = useSelector(getUserCountry);

  const isDrumOilFR = isDrumOil && userCountry === 'FR';

  const stockMessage = isDrumOilFR ? (
    <Text type="base" color="purple_dark" noWrap ellipsis>
      {t('catalog.reference.stock.out_of_stock.part_unavailable_within_six_business_days', 'Within 6 business days')}
    </Text>
  ) : (
    <>
      <Text type="base" color="grey35" displayStyle={'error'} noWrap data-cy={'out-of-stock-text'}>
        {t('catalog.reference.stock.out_of_stock', 'Out of stock:')}
      </Text>
      <Text type="base" color="grey35" noWrap ellipsis data-cy={'stocks-empty-text'}>
        {t('catalog.reference.stock.out_of_stock.part_unavailable', 'Part actually unavailable')}
      </Text>
    </>
  );

  return (
    <Flex align={'center'} gap={5} data-cy={'stocks-empty'}>
      {isDrumOilFR ? (
        <Icon IconComponent={GlobeIcon} size={20} color={theme.color.palette_purple_7} noPointer />
      ) : (
        <Icon
          IconComponent={isExadisPickUp ? ExadisStoreIcon : Ban}
          size={20}
          color={theme.color.error}
          noPointer
          data-cy={'stocks-empty-icon'}
        />
      )}
      <Box height={50} />
      {isExadisPickUp ? (
        <Text
          type="base"
          bold={isExadisPickUp}
          color="grey35"
          displayStyle={'error'}
          noWrap
          data-cy={'stocks-out-of-stock'}
        >
          {t('catalog.reference.stock.insufficient_exadis_quantity', 'Insufficient quantity for Exadis Pick-Up')}
        </Text>
      ) : (
        stockMessage
      )}
    </Flex>
  );
}
